import React from 'react';

interface TripTabsProps {
  activeTripType: 'selfBookings' | 'groupRequests';
  onTripTypeChange: (tripType: 'selfBookings' | 'groupRequests') => void;
  activeStatus: string;
  onStatusChange: (status: string) => void;
  selfBookingCounts: {
    all: number;
    inHouse: number;
    upcoming: number;
    pending: number;
    past: number;
  };
  groupRequestCounts: {
    all: number;
    requestReceived: number;
    inProgress: number;
    moreInfoRequired: number;
    awaitingConfirmation: number;
    confirmed: number;
    canceled: number;
  };
  loading?: boolean;
}

const TripTabs: React.FC<TripTabsProps> = ({
  activeTripType,
  onTripTypeChange,
  activeStatus,
  onStatusChange,
  selfBookingCounts,
  groupRequestCounts,
  loading = false,
}) => {
  // Define main trip type tabs
  const tripTypeTabs = [
    {
      key: 'selfBookings' as const,
      label: 'Self-Bookings',
      count: selfBookingCounts.all,
    },
    {
      key: 'groupRequests' as const,
      label: 'Group Requests',
      count: groupRequestCounts.all,
    },
  ];

  // Define status options based on active trip type
  const statusOptions =
    activeTripType === 'selfBookings'
      ? [
          { key: 'all', label: 'All', count: selfBookingCounts.all },
          {
            key: 'in-house',
            label: 'In House',
            count: selfBookingCounts.inHouse,
          },
          {
            key: 'upcoming',
            label: 'Upcoming',
            count: selfBookingCounts.upcoming,
          },
          {
            key: 'pending',
            label: 'Pending',
            count: selfBookingCounts.pending,
          },
          { key: 'past', label: 'Past', count: selfBookingCounts.past },
        ]
      : [
          { key: 'all', label: 'All', count: groupRequestCounts.all },
          {
            key: 'request-received',
            label: 'Received',
            count: groupRequestCounts.requestReceived,
          },
          {
            key: 'in-progress',
            label: 'In Progress',
            count: groupRequestCounts.inProgress,
          },
          {
            key: 'more-info-required',
            label: 'More Info',
            count: groupRequestCounts.moreInfoRequired,
          },
          {
            key: 'awaiting-confirmation',
            label: 'Awaiting',
            count: groupRequestCounts.awaitingConfirmation,
          },
          {
            key: 'confirmed',
            label: 'Confirmed',
            count: groupRequestCounts.confirmed,
          },
          {
            key: 'canceled',
            label: 'Canceled',
            count: groupRequestCounts.canceled,
          },
        ];

  return (
    <div className="space-y-4">
      {/* Trip Type Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tripTypeTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => onTripTypeChange(tab.key)}
              className={`border-b-2 px-1 py-3 font-arimo text-lg font-medium ${
                activeTripType === tab.key
                  ? 'border-accentGreen text-accentGreen'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label} ({loading ? '...' : tab.count})
            </button>
          ))}
        </nav>
      </div>

      {/* Status Filter Dropdown */}
      <div className="flex items-center space-x-4">
        <div className="relative w-48">
          <label
            className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-gray-500"
            htmlFor="status-filter"
          >
            Status
          </label>
          <select
            id="status-filter"
            className="block w-full rounded-md border-gray-300 text-sm shadow-sm"
            value={activeStatus}
            onChange={(e) => onStatusChange(e.target.value)}
          >
            {statusOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label} ({loading ? '...' : option.count})
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default TripTabs;
